// Lib
import React, { useCallback } from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import locale from 'date-fns/locale/en-US';
import { format } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// Assets
import { ReactComponent as Calendar } from '../../assets/svg/calendar.svg';

// Hooks
import useUser from '../../hooks/useUser';

// Styles
import { useStyles } from './DatePickerStyles';

const DEFAULT_STACK_WIDTH = 130;

function customAdapter(options) {
  const adapter = new AdapterDateFns(options);

  const constructDayObject = (day) => ({ charAt: () => day });

  return {
    ...adapter,

    getWeekdays() {
      const customWeekdays = adapter.getWeekdays();

      return customWeekdays.map((day) => constructDayObject(day));
    },
  };
}

locale.options.weekStartsOn = 0;

const DatePicker = ({
  date,
  width,
  placeholder,
  onChange = () => {},
  required,
  datesDisabled = [],
  placeholderGradientPercentage = 85,
  helperTextPosition = 'relative' /* relative or absolute to move next elements down (under date picker) or not to */,
  inSidebarOnMobile = false /* is datepicker inside sidebar in mobile mode (then special styling) */,
  ...props
}) => {
  const onErrorText = `Valid format: mm/dd/yyyy`;
  const theme = useTheme();
  const { isKiteUser } = useUser();
  const isMobileMode = useMediaQuery(theme.breakpoints.down('md'));
  const { classes } = useStyles({
    isKiteUser,
    isMobileMode,
    placeholderGradientPercentage,
    required,
    helperTextPosition,
    inSidebarOnMobile,
  });

  const shouldDisableDate = useCallback(
    (date) => {
      if (datesDisabled && datesDisabled[format(date, 'yyyy-MM-dd')]) {
        return true;
      }
    },
    [datesDisabled],
  );

  return (
    <LocalizationProvider dateAdapter={customAdapter} adapterLocale={locale}>
      <Stack width={width || DEFAULT_STACK_WIDTH} className={classes.root}>
        <DesktopDatePicker
          shouldDisableDate={shouldDisableDate}
          closeOnSelect={true}
          showToolbar={false}
          showDaysOutsideCurrentMonth
          inputFormat="MM/dd/yyyy"
          value={date || null}
          onChange={onChange}
          renderInput={(params) => (
            <TextField
              {...params}
              helperText={params.error && onErrorText}
              inputProps={{
                ...params.inputProps,
                placeholder,
              }}
            />
          )}
          InputAdornmentProps={{ position: 'start' }}
          PaperProps={{ classes: { root: classes.paper } }}
          DialogProps={{ classes: { root: classes.dialog } }}
          components={{
            OpenPickerIcon: Calendar,
          }}
          InputProps={{
            classes: { root: classes.root },
            startAdornment: <Calendar className={classes.calendarIcon} />,
          }}
          {...props}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default DatePicker;
